import axios from 'axios';

// =====================================
// BASE URL (DEV + PROD SAFE)
// =====================================

const getApiUrl = () => {
  const url = process.env.REACT_APP_API_URL;

  if (!url) {
    console.error('❌ REACT_APP_API_URL saknas!');
    return 'http://localhost:5000/api';
  }

  return url.replace(/\/$/, '');
};

const BASE_URL = getApiUrl();

console.log('🌐 API BASE_URL:', BASE_URL);

// =====================================
// API CLIENTS
// =====================================

const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

const apiPublic = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
});

// =====================================
// ACCESS TOKEN (memory only)
// =====================================

let accessToken = null;

export const setAccessToken = (token) => {
  accessToken = token;
};

export const clearAccessToken = () => {
  accessToken = null;
};

// =====================================
// REQUEST INTERCEPTOR
// =====================================

api.interceptors.request.use(
  (config) => {
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// =====================================
// REFRESH CONTROL
// =====================================

let isRefreshing = false;
let requestQueue = [];

// =====================================
// RESPONSE INTERCEPTOR
// =====================================

api.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest = error.config;

    // no response from server
    if (!error.response) {
      return Promise.reject(error);
    }

    const status = error.response.status;

    const isRefreshRequest =
      originalRequest.url?.includes('/auth/refresh');

    const blockedRoutes = [
      '/auth/login',
      '/auth/register',
    ];

    const isBlockedRoute = blockedRoutes.some((route) =>
      originalRequest.url?.includes(route)
    );

    // =================================
    // BLOCK LOGIN/REGISTER FROM REFRESH
    // =================================

    if (isBlockedRoute) {
      return Promise.reject(error);
    }

    // =================================
    // HANDLE 401 (AUTH EXPIRED)
    // =================================

    if (status === 401) {
      // refresh request failed → logout user
      if (isRefreshRequest) {
        clearAccessToken();
        return Promise.reject(error);
      }

      // prevent infinite retry loop
      if (originalRequest._retry) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      // =================================
      // IF ALREADY REFRESHING → QUEUE REQUEST
      // =================================

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          requestQueue.push((token) => {
            try {
              originalRequest.headers = {
                ...originalRequest.headers,
                Authorization: `Bearer ${token}`,
              };

              resolve(api(originalRequest));
            } catch (err) {
              reject(err);
            }
          });
        });
      }

      isRefreshing = true;

      try {
        console.log('🔄 Refreshing token...');

        const res = await apiPublic.post('/auth/refresh');

        const newAccessToken = res.data.accessToken;

        if (!newAccessToken) {
          throw new Error('No access token returned');
        }

        setAccessToken(newAccessToken);

        // resolve queued requests
        requestQueue.forEach((cb) => cb(newAccessToken));
        requestQueue = [];

        // retry original request
        originalRequest.headers = {
          ...originalRequest.headers,
          Authorization: `Bearer ${newAccessToken}`,
        };

        return api(originalRequest);

      } catch (refreshError) {
        console.error('❌ Refresh failed');

        clearAccessToken();
        requestQueue = [];

        if (typeof window !== 'undefined') {
          window.location.href = '/login';
        }

        return Promise.reject(refreshError);

      } finally {
        isRefreshing = false;
      }
    }

    // =================================
    // HANDLE 403 (BUSINESS LOGIC)
    // =================================
    // ex: "Kontot väntar på godkännande"
    if (status === 403) {
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

// =====================================
// EXPORTS
// =====================================

export default api;
export { apiPublic };